<template>
  <v-app-bar color="secondary" density="compact" :flat="true">
    <v-toolbar-title class="text-button">{{ icdPresentationString }}: {{ icdDescription }} - {{ selected_menu_item.title }}</v-toolbar-title>
    <v-btn variant="text" icon="mdi-filter"
           @click.stop="filter_drawer = !filter_drawer"
           :disabled="dashboard_7.visible"></v-btn>
    <v-btn variant="text" icon="mdi-information"
           @click.stop="information_drawer = !information_drawer"
           :disabled="dashboard_7.visible"></v-btn>

    <template v-slot:extension>
      <v-tabs v-model="selected_menu_item" align-tabs="title" show-arrows>
        <v-tab v-for="menu_item in menu_items" :key="menu_item.dashboard" :value="menu_item"
               :disabled="menu_item.accessRight && !isAuthorized(menu_item.accessRight)">
          {{ menu_item.title }}
        </v-tab>
      </v-tabs>
    </template>
  </v-app-bar>

  <v-window v-model="selected_menu_item" direction="horizontal">
    <v-window-item :key="dashboard_1.dashboard" :value="dashboard_1" transition="fade-transition"
                   reverse-transition="fade-transition" @group:selected="(event) => this.dashboard_1.visible = event.value">
      <CancerDemographicBaseline :icd="icd"
                                 :age-at-diagnosis="this.selected_age_at_diagnosis"
                                 :diagnosis-date="this.selected_diagnosis_date"
                                 :survival-time-in-years="selectedSurvivalTimeInYears"
                                 :metastasis-status-m="this.selected_metastasis_status_m"
                                 :metastasis-status-n="this.selected_metastasis_status_n"
                                 :atc-codes="this.selected_atc"
                                 :visible="this.dashboard_1.visible" v-if="!this.initializing"></CancerDemographicBaseline>
    </v-window-item>
    <v-window-item :key="dashboard_2.dashboard" :value="dashboard_2" transition="fade-transition"
                   reverse-transition="fade-transition" @group:selected="(event) => this.dashboard_2.visible = event.value">
      <CancerOverallSurvival :icd="icd"
                             :age-at-diagnosis="this.selected_age_at_diagnosis"
                             :diagnosis-date="this.selected_diagnosis_date"
                             :survival-time-in-years="selectedSurvivalTimeInYears"
                             :metastasis-status-m="this.selected_metastasis_status_m"
                             :metastasis-status-n="this.selected_metastasis_status_n"
                             :atc-codes="this.selected_atc"
                             :visible="this.dashboard_2.visible" v-if="!this.initializing"></CancerOverallSurvival>
    </v-window-item>
    <v-window-item :key="dashboard_3.dashboard" :value="dashboard_3" transition="fade-transition"
                   reverse-transition="fade-transition" @group:selected="(event) => this.dashboard_3.visible = event.value">
      <CancerTreatmentPatternInterventions :icd="icd"
                                           :age-at-diagnosis="this.selected_age_at_diagnosis"
                                           :diagnosis-date="this.selected_diagnosis_date"
                                           :survival-time-in-years="selectedSurvivalTimeInYears"
                                           :metastasis-status-m="this.selected_metastasis_status_m"
                                           :metastasis-status-n="this.selected_metastasis_status_n"
                                           :atc-codes="this.selected_atc"
                                           :visible="this.dashboard_3.visible" v-if="!this.initializing"></CancerTreatmentPatternInterventions>
    </v-window-item>
    <v-window-item :key="dashboard_4.dashboard" :value="dashboard_4" transition="fade-transition"
                   reverse-transition="fade-transition" @group:selected="(event) => this.dashboard_4.visible = event.value">
      <CancerTreatmentPaths :icd="icd"
                            :age-at-diagnosis="this.selected_age_at_diagnosis"
                            :diagnosis-date="this.selected_diagnosis_date"
                            :survival-time-in-years="selectedSurvivalTimeInYears"
                            :metastasis-status-m="this.selected_metastasis_status_m"
                            :metastasis-status-n="this.selected_metastasis_status_n"
                            :atc-codes="this.selected_atc"
                            :visible="this.dashboard_4.visible" v-if="!this.initializing"></CancerTreatmentPaths>
    </v-window-item>
    <v-window-item :key="dashboard_5.dashboard" :value="dashboard_5" transition="fade-transition"
                   reverse-transition="fade-transition" @group:selected="(event) => this.dashboard_5.visible = event.value">
      <CancerTreatmentPatternPrescribedDrugs :icd="icd"
                                             :age-at-diagnosis="this.selected_age_at_diagnosis"
                                             :diagnosis-date="this.selected_diagnosis_date"
                                             :survival-time-in-years="selectedSurvivalTimeInYears"
                                             :metastasis-status-m="this.selected_metastasis_status_m"
                                             :metastasis-status-n="this.selected_metastasis_status_n"
                                             :atc-codes="this.selected_atc"
                                             :visible="this.dashboard_5.visible" v-if="!this.initializing"></CancerTreatmentPatternPrescribedDrugs>
    </v-window-item>
    <v-window-item :key="dashboard_6.dashboard" :value="dashboard_6" transition="fade-transition"
                   reverse-transition="fade-transition" @group:selected="(event) => this.dashboard_6.visible = event.value">
      <CancerEpiChat :icd="icd"
                     :visible="this.dashboard_6.visible" v-if="!this.initializing"></CancerEpiChat>
    </v-window-item>
    <v-window-item :key="dashboard_7.dashboard" :value="dashboard_7" transition="fade-transition"
                   reverse-transition="fade-transition"
                   @group:selected="(event) => {
                     this.dashboard_7.visible = event.value;
                     this.information_drawer &&= !event.value;
                     this.filter_drawer &&= !event.value;
                   }">
      <ClinicalTrials :mesh-ids="meshIds"
                      :mesh-description="meshDescription"
                      :visible="this.dashboard_7.visible" v-if="!this.initializing"></ClinicalTrials>
    </v-window-item>
  </v-window>

  <v-navigation-drawer v-model="information_drawer" v-bind:width="500" location="right" temporary>
    <v-list density="compact" nav>
      <v-list-item prepend-icon="mdi-information" title="Information about the data set" nav></v-list-item>
    </v-list>

    <v-divider></v-divider>

    <v-list density="compact" nav>
      <v-list-item><strong>Population definition</strong></v-list-item>
      <v-list-item prepend-icon="mdi:mdi-minus">
        Individuals with {{ diseaseDescription }} (ICD10: {{ icdPresentationString }}, malignant primary tumors)
        have been identified in the Swedish cancer register.
      </v-list-item>
      <v-list-item prepend-icon="mdi:mdi-minus">
        Date of diagnosis in the cancer register has been used as index date. Only individuals with a valid Swedish
        personal identification number are included.
      </v-list-item>
      <!--
      <v-list-item prepend-icon="mdi:mdi-minus">
        A person can have multiple registered primary tumors (within {{ icdPresentationString }}) in the cancer
        register, within the time span.
      </v-list-item>
      -->
      <v-list-item prepend-icon="mdi:mdi-minus">
        From the patient register, the prescribed drug register, and the cause-of-death register, selected
        information occurring from (and including) the index date has been retrieved.
      </v-list-item>

      <v-divider :vertical="true"></v-divider>

      <!--
      <v-list-item><strong>Time periods</strong></v-list-item>
      <v-list-item prepend-icon="mdi:mdi-minus">
        The Swedish Cancer Register: 2010 - 2021 (whole years)
      </v-list-item>
      <v-list-item prepend-icon="mdi:mdi-minus">
        The Swedish Cause of Death Register: 2010 - 2021 (whole years)
      </v-list-item>
      <v-list-item prepend-icon="mdi:mdi-minus">
        Swedish Death Notices: January 2022 (what has been reported up until the date of data extraction)
      </v-list-item>
      <v-list-item prepend-icon="mdi:mdi-minus">
        The Swedish Prescribed Drug Register: 2010 - 2022 (whole years)
      </v-list-item>
      <v-list-item prepend-icon="mdi:mdi-minus">
        The Swedish Patient Register: 2010 - October 2022
      </v-list-item>
      -->

      <v-divider v-if="cancer_register_cleaning_report.length > 0" :vertical="true"></v-divider>
      <v-list-item v-if="cancer_register_cleaning_report.length > 0">
        <strong>Cancer register data cleaning report</strong>
      </v-list-item>
      <v-list-item v-for="cancer_register_cleaning_report_line in cancer_register_cleaning_report"
                   :key="cancer_register_cleaning_report_line"
                   :value="cancer_register_cleaning_report_line" prepend-icon="mdi:mdi-minus">
        {{ cancer_register_cleaning_report_line }}
      </v-list-item>

      <v-divider v-if="cancer_medicine_register_cleaning_report.length > 0" :vertical="true"></v-divider>
      <v-list-item v-if="cancer_medicine_register_cleaning_report.length > 0">
        <strong>Prescribed drug register data cleaning report</strong>
      </v-list-item>
      <v-list-item v-for="cancer_medicine_register_cleaning_report_line in cancer_medicine_register_cleaning_report"
                   :key="cancer_medicine_register_cleaning_report_line"
                   :value="cancer_medicine_register_cleaning_report_line" prepend-icon="mdi:mdi-minus">
        {{ cancer_medicine_register_cleaning_report_line }}
      </v-list-item>

      <v-divider v-if="cancer_treatment_register_cleaning_report.length > 0" :vertical="true"></v-divider>
      <v-list-item v-if="cancer_treatment_register_cleaning_report.length > 0">
        <strong>Hospital diagnoses and treatments data cleaning report</strong>
      </v-list-item>
      <v-list-item v-for="cancer_treatment_register_cleaning_report_line in cancer_treatment_register_cleaning_report"
                   :key="cancer_treatment_register_cleaning_report_line"
                   :value="cancer_treatment_register_cleaning_report_line" prepend-icon="mdi:mdi-minus">
        {{ cancer_treatment_register_cleaning_report_line }}
      </v-list-item>

      <v-divider v-if="cancer_care_time_register_cleaning_report.length > 0" :vertical="true"></v-divider>
      <v-list-item v-if="cancer_care_time_register_cleaning_report.length > 0">
        <strong>Patient register, care time, data cleaning report</strong>
      </v-list-item>
      <v-list-item v-for="cancer_care_time_register_cleaning_report_line in cancer_care_time_register_cleaning_report"
                   :key="cancer_care_time_register_cleaning_report_line"
                   :value="cancer_care_time_register_cleaning_report_line" prepend-icon="mdi:mdi-minus">
        {{ cancer_care_time_register_cleaning_report_line }}
      </v-list-item>
    </v-list>
  </v-navigation-drawer>

  <v-navigation-drawer v-model="filter_drawer" location="right" v-bind:width="500">
    <v-card class="mx-auto epix-filter" elevation="0" ref="filters">
      <v-row no-gutters>
        <v-col>
          <v-list nav dense light>
            <v-list-item prepend-icon="mdi-filter" title="Filters" nav></v-list-item>
            <v-divider></v-divider>
            <v-form ref="filter_form">
              <v-row>
                <v-col cols="9">
                  <v-combobox v-model="selected_age_at_diagnosis" :items="select_age_at_diagnosis_items"
                              label="Age at first diagnosis" :rules="[rules.required]" @click="$refs.filter_form.validate()"
                              multiple></v-combobox>
                </v-col>
                <v-col cols="3">
                  <v-btn variant="plain" @click="toggleAllNoneAgeGroups">All/None</v-btn>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="9">
                  <v-combobox v-model="selected_diagnosis_date" :items="select_diagnosis_date_items"
                              label="Year of first diagnosis" :rules="[rules.required]" @click="$refs.filter_form.validate()"
                              multiple></v-combobox>
                </v-col>
                <v-col cols="3">
                  <v-btn variant="plain" @click="toggleAllNoneYears">All/None</v-btn>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="9">
                  <v-combobox v-model="selected_survival_time_in_years" :items="select_survival_time_in_years_items"
                              label="Follow-up time (years) from first diagnosis" :rules="[rules.required]" @click="$refs.filter_form.validate()"
                              messages="0 means less than one year, 1 means between one year (inclusive) and two years (exclusive), etc."
                              multiple></v-combobox>
                </v-col>
                <v-col cols="3">
                  <v-btn variant="plain" @click="toggleAllNoneSurvivalTimeInYears">All/None</v-btn>
                </v-col>
              </v-row>
              <v-divider :vertical="true"></v-divider>
              <v-combobox v-model="selected_metastasis_status_m" :items="select_metastasis_status_m_items"
                          v-if="select_metastasis_status_m_items.length > 0"
                          label="Metastasis status (M) at first diagnosis" :rules="[rules.required]" @click="$refs.filter_form.validate()"
                          :messages="['M0: No distant metastasis', 'M1: Distant metastasis', 'MX: Unknown']"
                          multiple></v-combobox>
              <v-divider :vertical="true"></v-divider>
              <v-combobox v-model="selected_metastasis_status_n" :items="select_metastasis_status_n_items"
                          v-if="select_metastasis_status_n_items.length > 0"
                          label="Metastasis status (N) at first diagnosis" :rules="[rules.required]" @click="$refs.filter_form.validate()"
                          :messages="['N0: No regional metastasis', 'N1+: Regional metastasis', 'NX: Unknown']"
                          multiple></v-combobox>
              <v-divider :vertical="true"></v-divider>
              <v-combobox v-model="selected_atc" :items="select_atc_items"
                          v-if="select_atc_items.length > 0"
                          label="ATC (at any time during follow-up)"
                          :messages="atc_code_legend"
                          multiple></v-combobox>
            </v-form>
          </v-list>
        </v-col>
      </v-row>
      <v-row no-gutters>
        <v-col class="text-right">
          <vue-to-print :content="() => this.$refs['filters'].$el" :document-title="icd + '-' + 'filters'"
                        remove-after-print>
            <template v-slot:trigger>
              <v-btn variant="plain" icon="mdi-printer" class="end"></v-btn>
            </template>
          </vue-to-print>
        </v-col>
      </v-row>
    </v-card>
  </v-navigation-drawer>
</template>

<script>
import {defineComponent} from "vue";
import axios from "axios";
import CancerDemographicBaseline from '@/components/CancerDemographicBaseline.vue'
import CancerOverallSurvival from '@/components/CancerOverallSurvival.vue'
import CancerTreatmentPatternInterventions from '@/components/CancerTreatmentPatternInterventions.vue'
import CancerTreatmentPaths from '@/components/CancerTreatmentPaths.vue'
import CancerTreatmentPatternPrescribedDrugs from '@/components/CancerTreatmentPatternPrescribedDrugs.vue'
import CancerEpiChat from '@/components/CancerEpiChat.vue'
import ClinicalTrials from "@/components/ClinicalTrials.vue";
import { VueToPrint } from "vue-to-print";

const DASHBOARD_1 = {
  title: 'Epidemiology and baseline characteristics',
  dashboard: 'Dashboard1',
  visible: true
};

const DASHBOARD_2 = {
  title: 'Overall survival',
  dashboard: 'Dashboard2',
  visible: false
};

const DASHBOARD_3 = {
  title: 'Treatment pattern, interventions given in hospital',
  dashboard: 'Dashboard3',
  visible: false
};

const DASHBOARD_4 = {
  title: 'Treatment paths',
  dashboard: 'Dashboard4',
  visible: false
};

const DASHBOARD_5 = {
  title: 'Treatment pattern, prescribed drugs',
  dashboard: 'Dashboard5',
  visible: false
};

const DASHBOARD_6 = {
  title: 'Epi-chat',
  dashboard: 'Dashboard6',
  accessRight: 'epi-chat',
  visible: false
};

const DASHBOARD_7 = {
  title: 'Clinical trials',
  dashboard: 'Dashboard7',
  accessRight: 'clinical-trials',
  visible: false
};

export default defineComponent({
  name: 'CancerView',
  props: {
    icd: String,
    icdPresentationString: String,
    icdDescription: String,
    diseaseDescription: String,
    meshIds: Array,
    meshDescription: String,
    dashboard1Included: {
      type: Boolean,
      default: true
    },
    dashboard2Included: {
      type: Boolean,
      default: true
    },
    dashboard3Included: {
      type: Boolean,
      default: true
    },
    dashboard4Included: {
      type: Boolean,
      default: true
    },
    dashboard5Included: {
      type: Boolean,
      default: true
    },
    dashboard6Included: {
      type: Boolean,
      default: true
    },
    dashboard7Included: {
      type: Boolean,
      default: true
    }
  },
  components: {
    CancerDemographicBaseline,
    CancerOverallSurvival,
    CancerTreatmentPatternInterventions,
    CancerTreatmentPaths,
    CancerTreatmentPatternPrescribedDrugs,
    CancerEpiChat,
    VueToPrint,
    ClinicalTrials
  },
  computed: {
    selectedSurvivalTimeInYears() {
      return this.selected_survival_time_in_years.map(f => f.value)
    }
  },
  data: context => ({
    initializing: true,
    dashboard_1: DASHBOARD_1,
    dashboard_2: DASHBOARD_2,
    dashboard_3: DASHBOARD_3,
    dashboard_4: DASHBOARD_4,
    dashboard_5: DASHBOARD_5,
    dashboard_6: DASHBOARD_6,
    dashboard_7: DASHBOARD_7,
    selected_menu_item: DASHBOARD_1,
    cancer_register_cleaning_report: [],
    cancer_medicine_register_cleaning_report: [],
    cancer_treatment_register_cleaning_report: [],
    cancer_care_time_register_cleaning_report: [],
    select_age_at_diagnosis_items: [],
    selected_age_at_diagnosis: [],
    select_diagnosis_date_items: [],
    selected_diagnosis_date: [],
    select_survival_time_in_years_items: [],
    selected_survival_time_in_years: [],
    select_metastasis_status_m_items: [],
    selected_metastasis_status_m: [],
    select_metastasis_status_n_items: [],
    selected_metastasis_status_n: [],
    select_atc_items: [],
    atc_code_legend: [],
    selected_atc: [],
    rules: {
      required(value) {
        if (value instanceof Array && (value.length === 0)) {
          return 'Required.';
        }
        return !!value || 'Required.';
      }
    },
    information_drawer: false,
    filter_drawer: false,
    menu_items: [
      context.dashboard1Included ? DASHBOARD_1 : null,
      context.dashboard2Included ? DASHBOARD_2 : null,
      context.dashboard3Included ? DASHBOARD_3 : null,
      context.dashboard4Included ? DASHBOARD_4 : null,
      context.dashboard5Included ? DASHBOARD_5 : null,
      context.dashboard6Included ? DASHBOARD_6 : null,
      context.dashboard7Included ? DASHBOARD_7 : null
    ].filter(v => v !== null)
  }),
  methods: {
    isAuthorized(accessRight) {
      return this.$store.getters.stateUser
          && this.$store.getters.stateUser.access_rights
          && this.$store.getters.stateUser.access_rights.includes(accessRight)
    },
    toggleAllNoneAgeGroups() {
      if (this.selected_age_at_diagnosis.length === this.select_age_at_diagnosis_items.length) {
        this.selected_age_at_diagnosis = [];
      } else {
        this.selected_age_at_diagnosis = [...this.select_age_at_diagnosis_items]
      }
    },
    toggleAllNoneYears() {
      if (this.selected_diagnosis_date.length === this.select_diagnosis_date_items.length) {
        this.selected_diagnosis_date = [];
      } else {
        this.selected_diagnosis_date = [...this.select_diagnosis_date_items]
      }
    },
    toggleAllNoneSurvivalTimeInYears() {
      if (this.selected_survival_time_in_years.length === this.select_survival_time_in_years_items.length) {
        this.selected_survival_time_in_years = [];
      } else {
        this.selected_survival_time_in_years = [...this.select_survival_time_in_years_items]
      }
    },
    async loadCancerRegisterCleaningReport() {
      let data = axios.get('/cancer-registry-cleaning-report',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              icd: this.icd
            }
          });
      return data.then((response) => {
        this.cancer_register_cleaning_report = response.data;
      });
    },
    async loadCancerMedicineRegisterCleaningReport() {
      let data = axios.get('/cancer-medicine-registry-cleaning-report',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              icd: this.icd
            }
          });
      return data.then((response) => {
        this.cancer_medicine_register_cleaning_report = response.data;
      });
    },
    async loadCancerTreatmentRegisterCleaningReport() {
      let data = axios.get('/cancer-treatment-registry-cleaning-report',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              icd: this.icd
            }
          });
      return data.then((response) => {
        this.cancer_treatment_register_cleaning_report = response.data;
      });
    },
    async loadCancerCareTimeRegisterCleaningReport() {
      let data = axios.get('/cancer-care-time-registry-cleaning-report',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              icd: this.icd
            }
          });
      return data.then((response) => {
        this.cancer_care_time_register_cleaning_report = response.data;
      });
    },
    async loadAgeAtDiagnosis() {
      return axios.get('/cancer-ages-at-diagnosis',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              icd: this.icd
            }
          });
    },
    async loadDiagnosisYears() {
      return axios.get('/cancer-diagnosis-years',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              icd: this.icd
            }
          });
    },
    async loadSurvivalTimeInYears() {
      return axios.get('/cancer-survival-times-in-years',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              icd: this.icd
            }
          });
    },
    async loadMetastasisStatusM() {
      return axios.get('/cancer-metastasis-statuses-m',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              icd: this.icd
            }
          });
    },
    async loadMetastasisStatusN() {
      return axios.get('/cancer-metastasis-statuses-n',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              icd: this.icd
            }
          });
    },
    async loadAtcCodes() {
      return axios.get('/cancer-atc-codes',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              icd: this.icd
            }
          });
    },
    async loadAtcCodeLegend() {
      return axios.get('/cancer-atc-code-legend',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              icd: this.icd
            }
          });
    },
    async loadStaticData() {
      return await Promise.all([
        this.loadAgeAtDiagnosis(),
        this.loadDiagnosisYears(),
        this.loadSurvivalTimeInYears(),
        this.loadMetastasisStatusM(),
        this.loadMetastasisStatusN(),
        this.loadAtcCodes(),
        this.loadAtcCodeLegend()
      ]).then((response) => {
        this.select_age_at_diagnosis_items = response[0].data;
        this.selected_age_at_diagnosis = response[0].data;
        this.select_diagnosis_date_items = response[1].data;
        this.selected_diagnosis_date = response[1].data;
        this.select_survival_time_in_years_items = response[2].data;
        this.selected_survival_time_in_years = response[2].data;
        this.select_metastasis_status_m_items = response[3].data;
        this.selected_metastasis_status_m = response[3].data;
        this.select_metastasis_status_n_items = response[4].data;
        this.selected_metastasis_status_n = response[4].data;
        this.select_atc_items = response[5].data;
        this.atc_code_legend = response[6].data;
        console.log('Static data loaded: CancerView')
      })
    },
    async loadDynamicData() {
      return await Promise.all([
        this.loadCancerRegisterCleaningReport(),
        this.loadCancerMedicineRegisterCleaningReport(),
        this.loadCancerTreatmentRegisterCleaningReport(),
        this.loadCancerCareTimeRegisterCleaningReport()
      ]).then(() => {
        console.log('Dynamic data loaded: CancerView')
      })
    }
  },
  watch: {
    icd() {
      this.loadStaticData().then(() => {
        this.loadDynamicData()
      })
    }
  },
  async mounted() {
    this.initializing = true
    await this.loadStaticData().then(() => {
      this.initializing = false
      return this.loadDynamicData()
    }).finally(() => {
      this.initializing = false
    })
  }
})
</script>
